<!--
  Customer Portal — Outlier Threshold settings (Story 3.6, design-spec §3g / §5.5.1).
  pp-only (BR-07), theme tokens only (no prototype hex). The WRITE path for
  `outlier_threshold_customer`; the server does the is_outlier maths.
-->
<section class="pf-content" aria-labelledby="pf-settings-heading">
  <nav class="pf-settings-breadcrumb">
    <a routerLink="/customer-pricing" class="pf-settings-back">← Back to Customer Pricing</a>
  </nav>

  <h1 id="pf-settings-heading">Settings · Outlier Threshold</h1>
  <p class="pf-page-description">Configure the discount-change threshold that flags a supplier update as an outlier.</p>

  <p *ngIf="loadError" class="pf-empty" role="alert">
    Couldn't load the current threshold. Please reload the page and try again.
  </p>

  <div *ngIf="loading" class="pf-skeleton" aria-busy="true" aria-label="Loading">
    <div class="pf-skeleton-row"></div>
  </div>

  <div *ngIf="!loading && !loadError" class="pf-settings-card">
    <h2 class="pf-settings-section-title">Outlier Threshold</h2>

    <!-- Current state line — only once a threshold is configured (§3g). -->
    <p class="pf-settings-current" *ngIf="isConfigured">
      Current threshold: <strong>{{ currentThreshold }} pp</strong>
    </p>

    <div class="pf-settings-field">
      <label class="pf-settings-label" for="pf-threshold-input">Discount change (pp)</label>
      <dx-number-box
        [value]="$any(value)"
        [min]="0"
        [step]="0.1"
        [showSpinButtons]="false"
        format="#0.## 'pp'"
        placeholder="e.g. 5"
        [inputAttr]="{ id: 'pf-threshold-input', 'aria-label': 'Outlier threshold in percentage points' }"
        (onValueChanged)="onValueChanged($event)"
      ></dx-number-box>

      <!-- Helper copy: the rule when set, the "nothing flagged" note when not (§3g). -->
      <p class="pf-settings-helper" *ngIf="isConfigured || value !== null">
        A product is flagged when the absolute pp discount delta on any configured tier exceeds this threshold.
      </p>
      <p class="pf-settings-helper" *ngIf="!isConfigured && value === null">
        No threshold is currently set. All products will appear without outlier flags.
      </p>
    </div>

    <div class="pf-settings-actions">
      <dx-button
        text="Save threshold"
        type="default"
        stylingMode="contained"
        [disabled]="!canSave"
        (onClick)="save()"
        aria-label="Save the outlier threshold"
      ></dx-button>
      <dx-button
        text="Cancel"
        stylingMode="text"
        [disabled]="saving"
        (onClick)="cancel()"
      ></dx-button>
      <!-- Clear is a danger text link, visible only when a threshold is configured (§3g). -->
      <dx-button
        *ngIf="isConfigured"
        class="pf-settings-clear"
        text="Clear threshold"
        stylingMode="text"
        type="danger"
        [disabled]="saving"
        (onClick)="openClearConfirm()"
        aria-label="Clear the outlier threshold"
      ></dx-button>
    </div>
  </div>

  <!-- Clear-threshold confirmation dialog (§3g — unlike the supplier side, Clear is confirmed). -->
  <dx-popup
    [(visible)]="clearConfirmVisible"
    [width]="460"
    height="auto"
    [showTitle]="true"
    title="Clear outlier threshold"
    [dragEnabled]="false"
    [hideOnOutsideClick]="true"
  >
    <div *dxTemplate="let data of 'content'">
      <div class="pf-modal-body">
        <p>Clear the outlier threshold? Flags will be removed from every in-flight review until a new threshold is set.</p>
      </div>
      <div class="pf-modal-actions">
        <dx-button text="Cancel" stylingMode="text" [disabled]="saving" (onClick)="clearConfirmVisible = false"></dx-button>
        <dx-button text="Clear threshold" type="danger" [disabled]="saving" (onClick)="confirmClear()"></dx-button>
      </div>
    </div>
  </dx-popup>
</section>
